const SECTION_REPLACEMENTS = [
  { title: 'Key Features', pattern: /(^|[\n]| {2,}|[.!?]\s+)\*?\s*Key Features\s*:?\s*\*?/gi },
  { title: 'Key Features', pattern: /(^|[\n]|[ \t]+|[.!?]\s+)(?<!Key )\*?\s*Features\s*:\s*\*?/gi },
  { title: 'Perfect For', pattern: /(^|[\n]|[ \t]+|[.!?]\s+)\*?\s*Perfect For\s*:\s*\*?/gi },
  { title: 'Ideal For', pattern: /(^|[\n]|[ \t]+|[.!?]\s+)\*?\s*Ideal For\s*:\s*\*?/gi },
  { title: 'Best For', pattern: /(^|[\n]|[ \t]+|[.!?]\s+)\*?\s*Best For\s*:\s*\*?/gi },
];

const cleanInlineText = (value = '') =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')')
    .trim();

const normalizeDescriptionText = (value = '') => {
  if (!value) return '';

  let normalized = String(value)
    .replace(/\r\n?/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/[•●]/g, '-');

  SECTION_REPLACEMENTS.forEach(({ title, pattern }) => {
    normalized = normalized.replace(pattern, (_match, prefix) => `${prefix}\n\n${title}:\n`);
  });

  return normalized
    .replace(/[_*]+/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const trimLeadPriceCopy = (value = '') =>
  value.replace(/^.{0,90}\bKSh\s*[\d,]+\b[:\s-–—]*/i, '').trim();

const trimRepeatedProductName = (value = '', productName = '') => {
  const cleanedValue = cleanInlineText(value);
  const cleanedProductName = cleanInlineText(productName);

  if (!cleanedValue || !cleanedProductName) return cleanedValue;

  if (!cleanedValue.toLowerCase().startsWith(cleanedProductName.toLowerCase())) {
    return cleanedValue;
  }

  return cleanedValue.slice(cleanedProductName.length).replace(/^[-–—:,\s]+/, '').trim();
};

const parseFeatureItems = (body = '') => {
  const chunks = body
    .replace(/\s+-\s+/g, '\n- ')
    .split(/\n+| {2,}/)
    .map((chunk) => cleanInlineText(chunk.replace(/^-\s*/, '')))
    .filter(Boolean);

  return chunks.map((chunk) => {
    const colonMatch = chunk.match(/^([^:]{2,40}?)\s*:\s*(.+)$/);
    if (colonMatch) {
      return {
        label: cleanInlineText(colonMatch[1]),
        value: cleanInlineText(colonMatch[2]),
      };
    }

    const dashMatch = chunk.match(/^(.+?)\s+[\u2013\u2014-]\s+(.+)$/);
    if (dashMatch) {
      return {
        label: cleanInlineText(dashMatch[1]),
        value: cleanInlineText(dashMatch[2]),
      };
    }

    return {
      label: '',
      value: chunk,
    };
  });
};

export const parseProductDescription = (value = '', productName = '') => {
  const normalized = normalizeDescriptionText(value);

  if (!normalized) {
    return {
      summary: '',
      sections: [],
      plainText: '',
    };
  }

  const blocks = normalized
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  let summary = '';
  const sections = [];

  blocks.forEach((block, index) => {
    const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
    if (!lines.length) return;

    const headingMatch = lines[0].match(/^(Key Features|Perfect For|Ideal For|Best For):?$/i);
    if (!headingMatch) {
      if (!summary) {
        summary = lines.join(' ');
      } else {
        summary = `${summary} ${lines.join(' ')}`.trim();
      }
      return;
    }

    const title = headingMatch[1]
      .toLowerCase()
      .split(' ')
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
    const body = lines.slice(1).join(' ');
    const cleanedBody = cleanInlineText(body);

    if (!cleanedBody) return;

    if (title === 'Key Features') {
      const items = parseFeatureItems(body);
      const hasLabeledItems = items.some((item) => item.label);

      sections.push({
        key: `${title}-${index}`,
        title,
        ...(hasLabeledItems
          ? { items }
          : { text: items.map((item) => item.value).filter(Boolean).join(' ') }),
      });
      return;
    }

    sections.push({
      key: `${title}-${index}`,
      title,
      text: cleanedBody,
    });
  });

  const cleanedSummary = trimRepeatedProductName(trimLeadPriceCopy(summary), productName);
  const plainText = cleanInlineText([
    cleanedSummary,
    ...sections.flatMap((section) =>
      section.items?.length
        ? section.items.map((item) => (item.label ? `${item.label}: ${item.value}` : item.value))
        : section.text
          ? [`${section.title}: ${section.text}`]
          : []
    ),
  ].filter(Boolean).join(' '));

  return {
    summary: cleanedSummary || cleanInlineText(summary),
    sections,
    plainText,
  };
};

export const getProductDescriptionSummary = (value = '', productName = '') =>
  parseProductDescription(value, productName).summary;

export const getPlainProductDescription = (value = '', productName = '') =>
  parseProductDescription(value, productName).plainText;
